import { useEffect, useMemo, useState } from 'react';
import {
  BatteryCharging,
  Plus,
  Pencil,
  Trash2,
  Search,
  X,
  ToggleLeft,
  ToggleRight,
  Plug,
} from 'lucide-react';
import { adminApi, type EvModel, type EvModelInput } from '../../services/api';

const EMPTY_FORM: EvModelInput = {
  make: '',
  model: '',
  variant: '',
  yearFrom: null,
  yearTo: null,
  vehicleType: 'BEV',
  batteryKwh: 0,
  rangeWltpKm: 0,
  acChargerKw: null,
  dcChargerKw: null,
  active: true,
};

export default function EvModelsPage() {
  const [models, setModels] = useState<EvModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [editing, setEditing] = useState<EvModel | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<EvModelInput>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void load();
  }, []);

  async function load() {
    setLoading(true);
    try {
      const r = await adminApi.listEvModels();
      setModels(r.data);
    } finally {
      setLoading(false);
    }
  }

  const filtered = useMemo(() => {
    return models.filter((m) => {
      if (!showInactive && !m.active) return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        const hay = `${m.make} ${m.model} ${m.variant ?? ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [models, search, showInactive]);

  const grouped = useMemo(() => {
    const byMake: Record<string, EvModel[]> = {};
    for (const m of filtered) {
      (byMake[m.make] ||= []).push(m);
    }
    return Object.entries(byMake).sort(([a], [b]) => a.localeCompare(b));
  }, [filtered]);

  function openCreate() {
    setEditing(null);
    setCreating(true);
    setForm(EMPTY_FORM);
    setError(null);
  }

  function openEdit(m: EvModel) {
    setCreating(false);
    setEditing(m);
    setForm({
      make: m.make,
      model: m.model,
      variant: m.variant ?? '',
      yearFrom: m.yearFrom,
      yearTo: m.yearTo,
      vehicleType: m.vehicleType,
      batteryKwh: m.batteryKwh,
      rangeWltpKm: m.rangeWltpKm,
      acChargerKw: m.acChargerKw,
      dcChargerKw: m.dcChargerKw,
      active: m.active,
    });
    setError(null);
  }

  function closeModal() {
    setCreating(false);
    setEditing(null);
    setError(null);
  }

  async function handleSave() {
    setError(null);
    if (!form.make.trim() || !form.model.trim() || form.batteryKwh <= 0 || form.rangeWltpKm <= 0) {
      setError('Marque, modèle, capacité batterie et autonomie WLTP sont obligatoires.');
      return;
    }
    setSaving(true);
    try {
      const payload: EvModelInput = {
        ...form,
        variant: form.variant?.toString().trim() || null,
        yearFrom: form.yearFrom ?? null,
        yearTo: form.yearTo ?? null,
        acChargerKw: form.acChargerKw ?? null,
        dcChargerKw: form.dcChargerKw ?? null,
      };
      if (editing) {
        await adminApi.updateEvModel(editing.id, payload);
      } else {
        await adminApi.createEvModel(payload);
      }
      await load();
      closeModal();
    } catch (e) {
      const msg =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        (e instanceof Error ? e.message : 'Erreur');
      setError(msg);
    } finally {
      setSaving(false);
    }
  }

  async function handleToggleActive(m: EvModel) {
    await adminApi.updateEvModel(m.id, { active: !m.active });
    await load();
  }

  async function handleDelete(m: EvModel) {
    if (!confirm(`Supprimer ${m.make} ${m.model}${m.variant ? ` (${m.variant})` : ''} ?`)) return;
    try {
      await adminApi.deleteEvModel(m.id);
      await load();
    } catch (e) {
      const msg =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Suppression impossible';
      alert(msg);
    }
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-start justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <BatteryCharging className="h-7 w-7 text-emerald-600" />
            Catalogue véhicules électriques
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Référentiel global des modèles EV. Chaque tenant pioche dans cette liste pour qualifier ses véhicules.
            Capacité batterie et autonomie WLTP sont la source de vérité pour les estimations d'autonomie.
          </p>
        </div>
        <button
          onClick={openCreate}
          className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium"
        >
          <Plus className="h-4 w-4" />
          Ajouter un modèle
        </button>
      </div>

      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher (marque, modèle, variante)…"
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-emerald-500 focus:border-emerald-500"
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            className="rounded border-gray-300"
          />
          Inclure les inactifs
        </label>
        <span className="ml-auto text-sm text-gray-500">
          {filtered.length} modèle{filtered.length > 1 ? 's' : ''}
        </span>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500">Chargement…</div>
      ) : grouped.length === 0 ? (
        <div className="text-center py-12 text-gray-500">Aucun modèle.</div>
      ) : (
        <div className="space-y-4">
          {grouped.map(([make, items]) => (
            <div key={make} className="bg-white rounded-lg border border-gray-200">
              <div className="px-4 py-2 bg-gray-50 rounded-t-lg border-b border-gray-200 font-semibold text-gray-900">
                {make}
              </div>
              <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase text-gray-500">
                  <tr>
                    <th className="px-4 py-2">Modèle</th>
                    <th className="px-4 py-2">Variante</th>
                    <th className="px-4 py-2">Type</th>
                    <th className="px-4 py-2">Années</th>
                    <th className="px-4 py-2 text-right">Batterie</th>
                    <th className="px-4 py-2 text-right">WLTP</th>
                    <th className="px-4 py-2 text-right">AC / DC</th>
                    <th className="px-4 py-2 text-center">Actif</th>
                    <th className="px-4 py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((m) => (
                    <tr
                      key={m.id}
                      className={`border-t border-gray-100 ${
                        m.active ? '' : 'bg-gray-50 text-gray-400'
                      }`}
                    >
                      <td className="px-4 py-2 font-medium">{m.model}</td>
                      <td className="px-4 py-2">{m.variant || '—'}</td>
                      <td className="px-4 py-2">
                        <span
                          className={`text-xs px-1.5 py-0.5 rounded ${
                            m.vehicleType === 'BEV'
                              ? 'bg-emerald-100 text-emerald-700'
                              : 'bg-amber-100 text-amber-700'
                          }`}
                        >
                          {m.vehicleType}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-xs">
                        {m.yearFrom ?? '—'}
                        {m.yearTo ? `–${m.yearTo}` : ''}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums">{m.batteryKwh} kWh</td>
                      <td className="px-4 py-2 text-right tabular-nums">{m.rangeWltpKm} km</td>
                      <td className="px-4 py-2 text-right text-xs">
                        <Plug className="inline h-3 w-3 mr-1 text-gray-400" />
                        {m.acChargerKw ?? '—'} / {m.dcChargerKw ?? '—'} kW
                      </td>
                      <td className="px-4 py-2 text-center">
                        <button
                          onClick={() => handleToggleActive(m)}
                          className={m.active ? 'text-emerald-600' : 'text-gray-400'}
                          title={m.active ? 'Désactiver' : 'Réactiver'}
                        >
                          {m.active ? (
                            <ToggleRight className="h-5 w-5" />
                          ) : (
                            <ToggleLeft className="h-5 w-5" />
                          )}
                        </button>
                      </td>
                      <td className="px-4 py-2 text-right">
                        <div className="inline-flex gap-2">
                          <button
                            onClick={() => openEdit(m)}
                            className="text-gray-500 hover:text-emerald-600"
                            title="Modifier"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(m)}
                            className="text-gray-400 hover:text-red-600"
                            title="Supprimer"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}

      {(creating || editing) && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                {editing ? 'Modifier' : 'Nouveau modèle EV'}
              </h2>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600"
                disabled={saving}
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Marque *</label>
                  <input
                    value={form.make}
                    onChange={(e) => setForm({ ...form, make: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    placeholder="Tesla"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Modèle *</label>
                  <input
                    value={form.model}
                    onChange={(e) => setForm({ ...form, model: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    placeholder="Model 3"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Variante</label>
                  <input
                    value={form.variant ?? ''}
                    onChange={(e) => setForm({ ...form, variant: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    placeholder="Long Range AWD"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Année début</label>
                  <input
                    type="number"
                    value={form.yearFrom ?? ''}
                    onChange={(e) =>
                      setForm({ ...form, yearFrom: e.target.value ? Number(e.target.value) : null })
                    }
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    placeholder="2021"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Année fin</label>
                  <input
                    type="number"
                    value={form.yearTo ?? ''}
                    onChange={(e) =>
                      setForm({ ...form, yearTo: e.target.value ? Number(e.target.value) : null })
                    }
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    placeholder="(en cours)"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type *</label>
                  <select
                    value={form.vehicleType}
                    onChange={(e) =>
                      setForm({ ...form, vehicleType: e.target.value as 'BEV' | 'PHEV' })
                    }
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  >
                    <option value="BEV">BEV — 100% électrique</option>
                    <option value="PHEV">PHEV — Hybride rechargeable</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">État</label>
                  <select
                    value={form.active === false ? 'inactive' : 'active'}
                    onChange={(e) => setForm({ ...form, active: e.target.value === 'active' })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  >
                    <option value="active">Actif</option>
                    <option value="inactive">Inactif (caché des tenants)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Batterie (kWh) *
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={form.batteryKwh || ''}
                    onChange={(e) => setForm({ ...form, batteryKwh: Number(e.target.value) })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Autonomie WLTP (km) *
                  </label>
                  <input
                    type="number"
                    value={form.rangeWltpKm || ''}
                    onChange={(e) => setForm({ ...form, rangeWltpKm: Number(e.target.value) })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Charge AC (kW)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={form.acChargerKw ?? ''}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        acChargerKw: e.target.value ? Number(e.target.value) : null,
                      })
                    }
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Charge DC max (kW)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={form.dcChargerKw ?? ''}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        dcChargerKw: e.target.value ? Number(e.target.value) : null,
                      })
                    }
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                </div>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3">
                  {error}
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
              <button
                onClick={closeModal}
                className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
                disabled={saving}
              >
                Annuler
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 text-sm bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg disabled:opacity-50"
              >
                {saving ? 'Enregistrement…' : editing ? 'Enregistrer' : 'Créer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
